import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Button, IconButton, Snackbar, Tooltip } from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { ImageCropper } from "../ImageCropper";
import {
  getAttachmentBlob,
  updateAttachmentPreview,
} from "../../api/attachments";
import { renderPdfFirstPageToWebp } from "../../utils/pdfToWebp";
import type { AttachmentDto } from "../../api/types";

export type AttachmentPreviewEditorProps = {
  attachment: AttachmentDto;
  /**
   * `button` is the labelled form of the action, for the empty-state placeholder
   * and dialog forms; `icon` is the compact overlay that sits on top of a
   * preview that already exists.
   */
  variant?: "button" | "icon";
  /** Styling hook for the placeholder, which sits on a black backdrop. */
  onDark?: boolean;
};

/**
 * The one way to give an attachment a preview.
 *
 * Making a first preview and changing an existing one are the same operation —
 * rasterize the file, frame it, store the result — so they are one component
 * rather than two flows that drift apart. The only thing `previewVersion`
 * decides is the wording.
 */
export const AttachmentPreviewEditor = ({
  attachment,
  variant = "button",
  onDark = false,
}: AttachmentPreviewEditorProps) => {
  const queryClient = useQueryClient();
  const [rasterToCrop, setRasterToCrop] = useState<Blob | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasPreview = attachment.previewVersion > 0;
  const label = hasPreview ? "Edit preview…" : "Generate preview";

  const open = async () => {
    setError(null);
    setLoading(true);
    try {
      const original = await getAttachmentBlob(attachment);
      // The cropper works on a raster, so a PDF has to be rendered first. An
      // image already is one.
      setRasterToCrop(
        attachment.contentType === "application/pdf"
          ? await renderPdfFirstPageToWebp(original)
          : original,
      );
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not read the attachment",
      );
    } finally {
      setLoading(false);
    }
  };

  const save = async (preview: Blob) => {
    setError(null);
    try {
      await updateAttachmentPreview(attachment.id, preview);
      // Every consumer reads the attachment list, so refetching it is what puts
      // the new preview (and its bumped version) on screen everywhere.
      await queryClient.invalidateQueries({ queryKey: ["attachments"] });
      setRasterToCrop(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the preview");
    }
  };

  return (
    <>
      {variant === "icon" ? (
        <Tooltip title={label}>
          <IconButton
            onClick={open}
            disabled={isLoading}
            aria-label={label}
            sx={{
              color: "#fff",
              bgcolor: "rgba(0, 0, 0, 0.5)",
              "&:hover": { bgcolor: "rgba(0, 0, 0, 0.75)" },
            }}
          >
            <PhotoCameraIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="outlined"
          size="small"
          startIcon={<PhotoCameraIcon />}
          onClick={open}
          disabled={isLoading}
          sx={onDark ? { color: "grey.300", borderColor: "grey.600" } : {}}
        >
          {isLoading ? "Loading…" : label}
        </Button>
      )}
      <ImageCropper
        open={rasterToCrop !== null}
        file={rasterToCrop}
        onClose={() => setRasterToCrop(null)}
        onSave={save}
      />
      <Snackbar
        open={error !== null}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};
